import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'deliverer_token';

const dax = axios.create({ baseURL: API });
dax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

// Sucursales visibles en el checklist (mismo orden que en el resto del portal).
const BRANCHES = [
  { id: 'morelos', label: 'Morelos', color: 'emerald' },
  { id: 'america', label: 'América', color: 'sky' },
];

/**
 * "Preparar carga" — el entregador selecciona qué envíos va a llevar en su
 * turno actual y ve el checklist consolidado de mercancía a cargar en el
 * camión (agregando cantidades por producto/variante), SEPARADO POR SUCURSAL
 * para que sepa qué mercancía sale de Morelos y cuál de América.
 */
export default function DelivererLoadChecklist({ onBack }) {
  const [shipmentsByBranch, setShipmentsByBranch] = useState({ morelos: [], america: [] });
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeBranch, setActiveBranch] = useState(() => {
    const saved = localStorage.getItem('deliverer_load_branch');
    if (!saved || saved === 'all') return 'morelos';
    return saved;
  });

  useEffect(() => { localStorage.setItem('deliverer_load_branch', activeBranch); }, [activeBranch]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [morResp, ameResp] = await Promise.all([
          dax.get('/public/deliverer/pending-shipments?branch=morelos'),
          dax.get('/public/deliverer/pending-shipments?branch=america'),
        ]);
        const morShips = morResp.data.shipments || [];
        const ameShips = ameResp.data.shipments || [];
        setShipmentsByBranch({ morelos: morShips, america: ameShips });
        // Por defecto, todos seleccionados (de ambas sucursales)
        setSelected(new Set([...morShips, ...ameShips].map((s) => s.id)));
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudo cargar');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const currentShipments = useMemo(() => shipmentsByBranch[activeBranch] || [], [shipmentsByBranch, activeBranch]);
  const selectAllInBranch = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      currentShipments.forEach((s) => next.add(s.id));
      return next;
    });
  };
  const clearAllInBranch = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      currentShipments.forEach((s) => next.delete(s.id));
      return next;
    });
  };

  // Checklist consolidado POR SUCURSAL ACTIVA: agrupa items por (name + variant).
  // Usa expanded_items del backend cuando existan (los paquetes se explotan
  // a sus productos individuales para que la carga sea la mercancía real
  // que hay que subir al camión, no "un paquete").
  const checklist = useMemo(() => {
    const map = new Map();
    for (const s of currentShipments) {
      if (!selected.has(s.id)) continue;
      const items = Array.isArray(s.expanded_items) && s.expanded_items.length
        ? s.expanded_items
        : (Array.isArray(s.items) ? s.items : []);
      for (const it of items) {
        const name = it.product_name || it.name || 'Producto';
        const variant = it.variant_name || '';
        const key = variant ? `${name} — ${variant}` : name;
        const prev = map.get(key) || { name, variant, quantity: 0 };
        prev.quantity += Number(it.quantity || 0);
        map.set(key, prev);
      }
    }
    return Array.from(map.entries())
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [currentShipments, selected]);

  const totalUnits = checklist.reduce((acc, i) => acc + i.quantity, 0);
  const selectedInBranch = currentShipments.filter((s) => selected.has(s.id)).length;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-red-600 text-white px-4 pb-3 shadow flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button onClick={onBack} className="text-white/90 hover:text-white text-3xl leading-none w-10 h-10 flex items-center justify-center -ml-2 rounded-full active:bg-red-700" data-testid="load-back-btn" aria-label="Volver">‹</button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wide opacity-80">Portal Entregador</p>
          <h1 className="text-base sm:text-lg font-bold truncate">📦 Preparar carga</h1>
        </div>
      </header>

      {error && <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-xs text-rose-800" data-testid="load-error">{error}</div>}

      {/* Tabs de sucursal con badge de conteo */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs w-fit" data-testid="load-branch-tabs">
          <span className="text-[10px] uppercase text-slate-500 pl-1">Sucursal:</span>
          {BRANCHES.map((t) => {
            const count = (shipmentsByBranch[t.id] || []).length;
            const isActive = activeBranch === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveBranch(t.id)}
                className={`relative px-3 py-1 rounded-md font-medium ${isActive ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
                data-testid={`load-branch-tab-${t.id}`}
              >
                {t.label}
                {count > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow border border-white"
                    data-testid={`load-branch-tab-${t.id}-count`}
                    title={`${count} envío(s) pendiente(s) en ${t.label}`}
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Selector de envíos (de la sucursal activa) */}
        <div className="lg:w-[45%] bg-white lg:border-r border-slate-200 flex flex-col min-h-0">
          <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2 text-xs">
            <span className="text-slate-500">
              {selectedInBranch} / {currentShipments.length} seleccionados
            </span>
            <div className="flex-1" />
            <button onClick={selectAllInBranch} className="text-blue-600 hover:underline" data-testid="load-select-all">Todos</button>
            <span className="text-slate-300">·</span>
            <button onClick={clearAllInBranch} className="text-slate-500 hover:underline" data-testid="load-clear-all">Ninguno</button>
          </div>
          <div className="overflow-y-auto flex-1 p-3 space-y-2">
            {loading && <p className="text-sm text-slate-500 text-center py-8">Cargando…</p>}
            {!loading && currentShipments.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="load-empty">No hay envíos pendientes en esta sucursal.</p>
            )}
            {currentShipments.map((s) => {
              const isOn = selected.has(s.id);
              return (
                <label
                  key={s.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${isOn ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                  data-testid={`load-shipment-${s.id}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5 accent-emerald-600"
                    checked={isOn}
                    onChange={() => toggle(s.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm break-words">{s.client_name || 'Cliente'}</p>
                    {s.folio && <p className="text-[11px] text-slate-400">Folio: {s.folio}</p>}
                    {(s.shipping_address || s.shipping_colonia) && (
                      <p className="text-xs text-slate-600 mt-0.5 break-words">{s.shipping_address || s.shipping_colonia}</p>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">
                      {(s.expanded_items || s.items || []).length} línea(s) · {(s.expanded_items || s.items || []).reduce((a, i) => a + Number(i.quantity || 0), 0)} pza(s)
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Checklist consolidado de la sucursal activa */}
        <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
          <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">
              🧾 Cargar al camión — <span className="text-red-600">{BRANCHES.find((b) => b.id === activeBranch)?.label}</span>
            </span>
            <div className="flex-1" />
            <span className="text-xs text-slate-500" data-testid="load-total-units">Total: <b>{totalUnits}</b> pza(s)</span>
          </div>
          <div className="overflow-y-auto flex-1 p-3">
            {checklist.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-10" data-testid="load-checklist-empty">Selecciona envíos para ver la mercancía a cargar.</p>
            )}
            <ul className="space-y-1.5" data-testid="load-checklist">
              {checklist.map((item) => (
                <li
                  key={item.key}
                  className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  data-testid={`load-checklist-item-${item.name}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-red-600 text-white font-bold rounded-lg flex items-center justify-center text-sm">
                    {item.quantity}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 break-words">{item.name}</p>
                    {item.variant && <p className="text-xs text-slate-500 break-words">{item.variant}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {checklist.length > 0 && (
            <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-200 text-xs text-emerald-800 text-center">
              ✅ Revisa que cargues <b>{totalUnits}</b> pieza(s) de {BRANCHES.find((b) => b.id === activeBranch)?.label} antes de salir.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
